import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/LoadingSpinner';
import Image from '../../components/AppImage';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';


const Community = ({ hideHeader = false, onUserClick }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [searchQuery, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, first_name, last_name, email, location, avatar_url, experience_level, created_at, phone')
        ?.order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Error fetching users:', fetchError);
        setError('Kullanıcılar yüklenemedi.');
        return;
      }

      // Convert snake_case to camelCase
      const formattedUsers = data?.map(user => {
        const firstLast = [user?.first_name, user?.last_name]?.filter(Boolean)?.join(' ')?.trim();
        const fullName = user?.full_name?.trim() || firstLast || null;
        return {
          id: user?.id,
          firstName: user?.first_name,
          lastName: user?.last_name,
          fullName: fullName || 'İsimsiz Kullanıcı',
          location: user?.location,
          avatarUrl: user?.avatar_url,
          experienceLevel: user?.experience_level,
          phone: user?.phone,
          memberSince: user?.created_at ? new Date(user?.created_at)?.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' }) : 'Bilinmiyor'
        };
      }) || [];

      setUsers(formattedUsers);
      setFilteredUsers(formattedUsers);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    if (!searchQuery?.trim()) {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery?.toLowerCase()?.trim();
    const filtered = users?.filter(user => {
      const fullName = user?.fullName?.toLowerCase() || '';
      const location = user?.location?.toLowerCase() || '';
      return fullName?.includes(query) || location?.includes(query);
    });

    setFilteredUsers(filtered);
  };

  const getInitials = (fullName) => {
    if (!fullName) return 'U';
    const names = fullName?.split(' ');
    if (names?.length >= 2) {
      return `${names?.[0]?.[0] || ''}${names?.[1]?.[0] || ''}`?.toUpperCase();
    }
    return fullName?.[0]?.toUpperCase() || 'U';
  };

  const getExperienceLevelLabel = (level) => {
    const levels = {
      beginner: 'Başlangıç',
      intermediate: 'Orta',
      advanced: 'İleri',
      expert: 'Uzman'
    };
    return levels?.[level] || 'Belirtilmemiş';
  };

  const getExperienceLevelColor = (level) => {
    const colors = {
      beginner: 'bg-blue-100 text-blue-700',
      intermediate: 'bg-green-100 text-green-700',
      advanced: 'bg-orange-100 text-orange-700',
      expert: 'bg-purple-100 text-purple-700'
    };
    return colors?.[level] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className={hideHeader ? "" : "min-h-screen bg-background"}>
        {!hideHeader && <Header />}
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <LoadingSpinner message="Topluluk üyeleri yükleniyor..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={hideHeader ? "" : "min-h-screen bg-background"}>
        {!hideHeader && <Header />}
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <p className="text-error text-lg mb-4">{error}</p>
            <Button onClick={fetchUsers} variant="primary">
              <Icon name="RefreshCw" size={20} className="mr-2" />
              Tekrar Dene
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={hideHeader ? "" : "min-h-screen bg-background"}>
      {!hideHeader && <Header />}
      
      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            Topluluk
          </h1>
          <p className="text-muted-foreground">
            SUP platformundaki tüm üyeleri keşfedin ve bağlantı kurun
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-2xl">
            <Icon 
              name="Search" 
              size={20} 
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
            />
            <input
              type="text"
              placeholder="İsim veya konum ile ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-muted-foreground">
            <span className="font-semibold text-foreground">{filteredUsers?.length}</span> üye bulundu
          </p>
        </div>

        {/* User Grid */}
        {filteredUsers?.length === 0 ? (
          <div className="text-center py-16">
            <Icon name="Users" size={64} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Kullanıcı Bulunamadı
            </h3>
            <p className="text-muted-foreground mb-4">
              Arama kriterlerinize uygun kullanıcı bulunamadı.
            </p>
            <Button onClick={() => setSearchQuery('')} variant="outline">
              Aramayı Temizle
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {filteredUsers?.map((member) => {
              const cardContent = (
                <div className="bg-card rounded-xl shadow-organic hover:shadow-organic-lg transition-smooth overflow-hidden group cursor-pointer">
                  <div className="p-6">
                    {/* Avatar */}
                    <div className="flex justify-center mb-4">
                      {member?.avatarUrl ? (
                        <Image
                          src={member?.avatarUrl}
                          alt={member?.fullName}
                          className="w-24 h-24 rounded-full object-cover border-4 border-primary/10 group-hover:border-primary/30 transition-colors"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center border-4 border-primary/10 group-hover:border-primary/30 transition-colors">
                          <span className="text-2xl font-bold text-primary">
                            {getInitials(member?.fullName)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* User Info */}
                    <div className="text-center">
                      <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                        {member?.fullName}
                      </h3>

                      {/* Location */}
                      {member?.location && (
                        <div className="flex items-center justify-center text-muted-foreground mb-3">
                          <Icon name="MapPin" size={16} className="mr-1" />
                          <span className="text-sm">{member?.location}</span>
                        </div>
                      )}

                      {/* Experience Level Badge */}
                      {member?.experienceLevel && (
                        <div className="flex justify-center mb-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getExperienceLevelColor(member?.experienceLevel)}`}>
                            {getExperienceLevelLabel(member?.experienceLevel)}
                          </span>
                        </div>
                      )}

                      {/* Member Since */}
                      <div className="flex items-center justify-center text-muted-foreground text-sm">
                        <Icon name="Calendar" size={14} className="mr-1" />
                        <span>Üye: {member?.memberSince}</span>
                      </div>

                      {/* Phone */}
                      {member?.phone && (
                        <div className="flex items-center justify-center text-muted-foreground text-sm mt-2">
                          <Icon name="Phone" size={14} className="mr-1" />
                          <span>{member?.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* View Profile Footer */}
                  <div className="bg-primary/5 px-6 py-3 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                    <span className="text-sm font-medium mr-2">Profili Görüntüle</span>
                    <Icon name="ArrowRight" size={16} />
                  </div>
                </div>
              );

              return onUserClick ? (
                <div key={member?.id} onClick={() => onUserClick(member?.id)}>
                  {cardContent}
                </div>
              ) : (
                <Link key={member?.id} to={`/profile/${member?.id}`}>
                  {cardContent}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Community;